import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleOutline,
  WarningAmberOutlined,
} from '@mui/icons-material';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { DialogTopBar } from '../components/core/DialogTopBar';

type IResponse = 'aborted' | 'confirmed' | 'rejected';

type Severity = 'info' | 'warning' | 'danger';

type Params = {
  title: string;
  message: string | JSX.Element;
  confirmButtonText: string;
  rejectButtonText?: string;
  severity?: Severity;
};

interface ConfirmationContextType {
  showConfirmation: (props: Params) => Promise<IResponse>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(
  undefined
);

interface ConfirmationProviderProps {
  children: ReactNode;
}

const iconForSeverity = (severity: Severity) => {
  switch (severity) {
    case 'warning':
    case 'danger':
      return <WarningAmberOutlined />;
    case 'info':
    default:
      return <CheckCircleOutline />;
  }
};

const confirmColorForSeverity = (
  severity: Severity
): 'primary' | 'warning' | 'error' => {
  switch (severity) {
    case 'danger':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'primary';
  }
};

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<
    | (Params & {
        resolve: (_: IResponse) => void;
      })
    | null
  >(null);

  const showConfirmation = (props: Params): Promise<IResponse> => {
    return new Promise<IResponse>(resolve => {
      setDialogProps({ ...props, resolve });
      setOpen(true);
    });
  };

  const handleClose = (response: IResponse) => {
    setOpen(false);
    if (dialogProps?.resolve) {
      dialogProps.resolve(response);
    }
    setDialogProps(null);
  };

  const severity: Severity = dialogProps?.severity ?? 'info';

  return (
    <ConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      {dialogProps && (
        <Dialog
          open={open}
          onClose={() => handleClose('aborted')}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: theme => ({
              borderRadius: 3,
              boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
            }),
          }}
        >
          <DialogTopBar
            icon={iconForSeverity(severity)}
            title={dialogProps.title}
            onClose={() => handleClose('aborted')}
          />
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2.5, sm: 3 } }}>
            <DialogContentText>{dialogProps.message}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
            {dialogProps.rejectButtonText && (
              <Button
                variant='outlined'
                onClick={() => handleClose('rejected')}
              >
                {dialogProps.rejectButtonText}
              </Button>
            )}
            <Button
              variant='contained'
              color={confirmColorForSeverity(severity)}
              onClick={() => handleClose('confirmed')}
              autoFocus
            >
              {dialogProps.confirmButtonText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error(
      'useConfirmation must be used within a ConfirmationProvider'
    );
  }
  return context;
};
